import { MenuItem, TextField } from '@material-ui/core'
import {
  Button,
  Div,
  Flex,
  Input,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import React from 'react'
import { getDetailedCountries } from 'src/graphql'
import { formatCountriesAndRegions } from 'src/helpers/formatCountriesAndRegions'
import {
  usePersistedUserData,
  useQueryFutures,
  useSelector,
  useState,
} from 'src/hooks'
import { AffiliateFormInfo } from 'src/marketing/plans/components/AffiliateForm/AffiliateForm'
import { FlexRow } from 'src/styled'

interface AffiliateAddressProps {
  affiliateInfo: AffiliateFormInfo
  onFinishAccount: (info: AffiliateFormInfo) => void
  onBack: () => void
}

interface AffiliateAddressFormState {
  firstName: string
  lastName: string
  company: string
  address1: string
  address2: string
  city: string
  state: string
  postalCode: string
  country: string
}

const AffiliateAddress: React.FC<AffiliateAddressProps> = props => {
  const { affiliateInfo, onFinishAccount, onBack } = props

  const { account } = usePersistedUserData()

  const [details, setDetails] = useState({
    firstName: affiliateInfo?.user.firstName || account?.firstName || '',
    lastName: affiliateInfo?.user.lastName || account?.lastName || '',
    company:
      affiliateInfo?.user.company || account?.shippingAddress?.company || '',
    address1:
      affiliateInfo?.user.address1 || account?.shippingAddress?.address1 || '',
    address2:
      affiliateInfo?.user.address2 || account?.shippingAddress?.address2 || '',
    city: affiliateInfo?.user.city || account?.shippingAddress?.city || '',
    state: affiliateInfo?.user.state || account?.shippingAddress?.state || '',
    postalCode:
      affiliateInfo?.user.postalCode ||
      account?.shippingAddress?.postalCode ||
      '',
    country:
      affiliateInfo?.user.country || account?.shippingAddress?.country || '',
  })

  const handleChange = (
    name: keyof AffiliateAddressFormState,
    value: string,
  ) => {
    setDetails({ ...details, [name]: value })
  }

  const [detailedCountries] = useQueryFutures(getDetailedCountries())
  const { countries, regions, isRegionsUnavailable } = detailedCountries.value
    ? formatCountriesAndRegions(
        detailedCountries.value,
        details.country ?? 'US',
      )
    : { countries: [], regions: undefined, isRegionsUnavailable: false }

  const isMobile = useSelector(state => state.window.isMobile)

  const isDisabled =
    !details.firstName ||
    !details.lastName ||
    !details.address1 ||
    !details.city ||
    !details.state ||
    !regions?.includes(details.state) ||
    !details.postalCode ||
    !details.country

  const onSubmit = () => {
    if (isDisabled) {
      return
    }
    onFinishAccount({
      ...affiliateInfo,
      user: {
        firstName: details.firstName,
        lastName: details.lastName,
        company: details.company,
        address1: details.address1,
        address2: details.address2,
        city: details.city,
        state: details.state,
        postalCode: details.postalCode,
        country: details.country,
      },
    })
    return
  }

  return (
    <form onSubmit={onSubmit}>
      <Flex width="100%" flexDirection="column">
        <Input
          onChange={firstName => handleChange('firstName', firstName)}
          type="text"
          name="firstName"
          value={details.firstName}
          placeholder="First Name"
          focusColor="primaryBrand"
          isFullWidth={true}
        />
        <Spacer space={'x1'} />
        <Input
          onChange={lastName => handleChange('lastName', lastName)}
          type="text"
          name="lastName"
          value={details.lastName}
          placeholder="Last Name"
          focusColor="primaryBrand"
          isFullWidth={true}
        />
        <Spacer space={'x1'} />
        <Input
          onChange={company => handleChange('company', company)}
          type="text"
          name="company"
          value={details.company}
          placeholder="Company"
          focusColor="primaryBrand"
          isFullWidth={true}
        />
        <Text
          type={'footnote'}
          content={
            'Note: Please only use this field if you are using your EIN.'
          }
          outset="x1"
        />
        <Spacer space={'x1'} />
        <Input
          onChange={address1 => handleChange('address1', address1)}
          type="text"
          name="address1"
          value={details.address1}
          placeholder="Address 1"
          focusColor="primaryBrand"
          isFullWidth={true}
        />
        <Spacer space={'x1'} />
        <Input
          onChange={address2 => handleChange('address2', address2)}
          type="text"
          name="address2"
          value={details.address2}
          placeholder="Address 2"
          focusColor="primaryBrand"
          isFullWidth={true}
        />
        <Spacer space={'x1'} />
        <Input
          onChange={city => handleChange('city', city)}
          type="text"
          name="city"
          value={details.city}
          placeholder="City"
          focusColor="primaryBrand"
          isFullWidth={true}
        />
        <Spacer space={'x1'} />
        <FlexRow>
          <TextField
            style={{ width: '50%' }}
            placeholder="Country"
            id={'country_field'}
            select={true}
            value={details.country}
            name="country"
            onChange={event => handleChange('country', event.target.value)}
            helperText={
              !regions?.includes(details.state)
                ? 'Please select a Country'
                : undefined
            }
          >
            {(countries || ['United States']).map((country, index) => (
              <MenuItem
                id={country.split(' ').join('_').toLowerCase()}
                key={index}
                value={country}
              >
                {country}
              </MenuItem>
            ))}
          </TextField>
          {!isRegionsUnavailable && (
            <TextField
              style={{ width: '50%' }}
              id={'state_field'}
              select={true}
              onChange={event => handleChange('state', event.target.value)}
              value={details.state}
              name="state"
              placeholder="State"
              helperText={
                !regions?.includes(details.state)
                  ? 'Please select a region'
                  : undefined
              }
            >
              {(regions || []).map((region, index) => (
                <MenuItem
                  id={region.split(' ').join('_').toLowerCase()}
                  key={index}
                  value={region}
                >
                  {region}
                </MenuItem>
              ))}
            </TextField>
          )}
        </FlexRow>

        <Spacer space={'x1'} />
        <Input
          onChange={postalCode => handleChange('postalCode', postalCode)}
          type="text"
          name="postalCode"
          value={details.postalCode}
          placeholder="Postal Code"
          focusColor="primaryBrand"
          isFullWidth={true}
        />
        <Spacer space={'x1'} />
      </Flex>
      <Flex width="100%" flexDirection="column">
        <Spacer space={isMobile ? 'x1' : 'x4'} />
        <Div width="100%" id="continue_btn">
          <Button
            fullWidth={true}
            type={'primary'}
            onClick="submitForm"
            title={'Continue'}
            disabled={isDisabled}
          />
        </Div>
        <Spacer space={isMobile ? 'x1' : 'x4'} />
        <Div width="100%" id="back_btn">
          <Button
            fullWidth={true}
            type={'secondary'}
            onClick={() => onBack()}
            title={'Back'}
          />
        </Div>
      </Flex>
    </form>
  )
}

export default AffiliateAddress
